import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Truck, RotateCcw } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";
import { formatDate } from "@/lib/date-formatters";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import OrdersFilterBar from "@/components/admin/OrdersFilterBar";
import BulkImportToShippingDialog from "@/components/orders/BulkImportToShippingDialog";

interface Order {
  id: number;
  first_name: string;
  last_name: string;
  status: "new" | "processing" | "fulfilled" | "cancelled";
  total: number;
  created_at: string;
  tracking_number: string | null;
  shipping_company: string | null;
  xexpress_announcement_id: number | null;
  is_pickup: number;
  items: { price: number }[];
}

const statusColors: Record<string, string> = {
  new: "bg-blue-50 text-blue-700",
  processing: "bg-yellow-50 text-yellow-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-700",
};

const statusLabels: Record<string, string> = {
  new: "Nova",
  processing: "U obradi",
  fulfilled: "Isporučena",
  cancelled: "Otkazana",
};

export default function Orders() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [shippingFilter, setShippingFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const { data: orders = [] } = useQuery<Order[]>({
    queryKey: ["admin-orders"],
    queryFn: () => apiFetch<Order[]>("/api/admin/orders"),
  });

  const resetMutation = useMutation({
    mutationFn: (orderIds: number[]) =>
      apiFetch("/api/admin/orders/reset-shipping", {
        method: "POST",
        body: JSON.stringify({ orderIds }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      setSelectedIds(new Set());
    },
  });

  const filtered = useMemo(() => {
    let result = orders;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (o) =>
          o.first_name.toLowerCase().includes(q) ||
          o.last_name.toLowerCase().includes(q) ||
          `${o.first_name} ${o.last_name}`.toLowerCase().includes(q) ||
          String(o.id).includes(q) ||
          (o.tracking_number && o.tracking_number.toLowerCase().includes(q)) ||
          (o.xexpress_announcement_id !== null && String(o.xexpress_announcement_id).includes(q))
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((o) => o.status === statusFilter);
    }
    if (shippingFilter !== "all") {
      result = result.filter((o) =>
        shippingFilter === "pickup" ? o.is_pickup === 1 : o.is_pickup === 0
      );
    }
    return result;
  }, [orders, searchQuery, statusFilter, shippingFilter]);

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((o) => o.id)));
    }
  }

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const someSelected = selectedIds.size > 0;

  const selectedArray = Array.from(selectedIds);
  const someSelectedHaveTracking = selectedArray.some((id) =>
    orders.find((o) => o.id === id)?.tracking_number
  );

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold">Narudžbe</h1>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selectedIds.size} odabrano</span>
            <Button size="sm" onClick={() => setImportDialogOpen(true)}>
              <Truck className="size-4" />
              Pošalji kuriru
            </Button>
            {someSelectedHaveTracking && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!confirm(`Resetovati shipping za ${selectedIds.size} narudžbi?`)) return;
                  resetMutation.mutate(selectedArray);
                }}
                disabled={resetMutation.isPending}
              >
                <RotateCcw className="size-4" />
                Reset
              </Button>
            )}
          </div>
        )}
      </div>

      <OrdersFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        shippingFilter={shippingFilter}
        onShippingFilterChange={setShippingFilter}
      />

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {orders.length === 0 ? "Nema narudžbi." : "Nema rezultata pretrage."}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3 md:hidden">
            {filtered.map((order) => (
              <div key={order.id} className="bg-white rounded-lg border border-border/60 p-3 flex items-center gap-3">
                <Checkbox
                  checked={selectedIds.has(order.id)}
                  onCheckedChange={() => toggleSelect(order.id)}
                  className="shrink-0"
                />
                <Link
                  to="/admin/orders/$id"
                  params={{ id: String(order.id) }}
                  className="flex-1 flex items-center gap-3 min-w-0 active:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">#{order.id}</span>
                      <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full", statusColors[order.status])}>
                        {statusLabels[order.status]}
                      </span>
                      {order.tracking_number && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                          {order.tracking_number}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {order.first_name} {order.last_name} · {formatDate(order.created_at)}
                    </p>
                  </div>
                  <span className="text-sm font-semibold shrink-0">{formatPrice(order.total)} KM</span>
                  <ChevronRight className="size-4 text-muted-foreground shrink-0" />
                </Link>
              </div>
            ))}
          </div>

          <div className="hidden md:block rounded-lg border border-border/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={
                        allSelected
                          ? true
                          : someSelected && !allSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Narudžba</th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Kupac</th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Datum</th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Artikli</th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Ukupno</th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-muted-foreground">Tracking</th>
                  <th className="w-10 px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {filtered.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selectedIds.has(order.id)}
                        onCheckedChange={() => toggleSelect(order.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Link to="/admin/orders/$id" params={{ id: String(order.id) }} className="font-medium hover:underline">
                        #{order.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{order.first_name} {order.last_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(order.created_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{order.items.length}</td>
                    <td className="px-4 py-3 font-medium">{formatPrice(order.total)} KM</td>
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", statusColors[order.status])}>
                        {statusLabels[order.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {order.tracking_number ? (
                        <span className="text-xs font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                          {order.tracking_number}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <Link to="/admin/orders/$id" params={{ id: String(order.id) }}>
                        <ChevronRight className="size-4 text-muted-foreground" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <BulkImportToShippingDialog
        selectedOrderIds={selectedArray}
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
